import { createActionGroup, emptyProps, props } from '@ngrx/store';

import {
  AgentDto,
  AgentInputDto,
  AgentInstructionDto,
  AgentPromptDto,
  AgentStatusType,
  AgentVersionDto,
  AgentVersionHistoryDto,
  CreateAgentPayload,
  CreateUpdateAgentInputPayload,
  CreateUpdateAgentInstructionPayload,
  CreateUpdateAgentPromptPayload,
  UpdateAgentPayload,
} from './models/agent.model';

export const AgentsActions = createActionGroup({
  source: 'Agents',
  events: {
    Init: emptyProps(),
    'Get Agents': props<{ tenantUuid: string; subscriptionUuid: string }>(),
    'Load Success': props<{ agents: readonly AgentDto[] }>(),
    'Load Failure': props<{ error: unknown }>(),
    'Create Agent': props<{
      tenantUuid: string;
      createAgent: CreateAgentPayload;
      createInstructions: CreateUpdateAgentInstructionPayload[];
      createPrompts: CreateUpdateAgentPromptPayload[];
      createInputs: CreateUpdateAgentInputPayload[];
      createVersion?: boolean;
    }>(),
    'Create Agent Success': props<{ agent: AgentDto }>(),
    'Create Agent Failure': props<{ error: unknown }>(),
    'Delete Agent': props<{ tenantUuid: string; agentUuid: string }>(),
    'Delete Agent Success': props<{ agentUuid: string }>(),
    'Delete Agent Failure': props<{ error: unknown; agentUuid: string }>(),
    'Update Agent': props<{
      tenantUuid: string;
      agentUuid: string;
      changes: UpdateAgentPayload;
      createVersion?: boolean;
    }>(),
    'Update Agent Success': props<{ agent: AgentDto }>(),
    'Update Agent Failure': props<{ error: unknown; agentUuid: string }>(),
    'Get Agent Hubs': props<{ tenantUuid: string }>(),
    'Get Agent Hubs Success': props<{ agentHubs: readonly string[] }>(),
    'Get Agent Hubs Failure': props<{ error: unknown }>(),
    'Get Agent Detail': props<{ tenantUuid: string; agentUuid: string }>(),
    'Get Agent Success': props<{ agent: AgentDto }>(),
    'Get Agent Failure': props<{ error: unknown }>(),
    'Get Instruction Detail': props<{ tenantUuid: string; agentUuid: string }>(),
    'Get Instruction Success': props<{ agentInstructions: AgentInstructionDto[] }>(),
    'Get Instruction Failure': props<{ error: unknown }>(),
    'Update Instruction': props<{
      tenantUuid: string;
      agentUuid: string;
      agentInstructionId: number;
      changes: CreateUpdateAgentInstructionPayload;
    }>(),
    'Update Instruction Success': props<{ agentInstruction: AgentInstructionDto }>(),
    'Update Instruction Failure': props<{ error: unknown }>(),
    'Create Instruction': props<{
      tenantUuid: string;
      agentUuid: string;
      changes: CreateUpdateAgentInstructionPayload;
      createVersion?: boolean;
    }>(),
    'Create Instruction Success': props<{
      agentInstruction: AgentInstructionDto;
      createVersion?: boolean;
    }>(),
    'Create Instruction Failure': props<{ error: unknown }>(),
    'Delete Instruction': props<{
      tenantUuid: string;
      agentUuid: string;
      agentInstructionId: number;
    }>(),
    'Delete Instruction Success': props<{
      agentInstructionId: number;
    }>(),
    'Delete Instruction Failure': props<{ error: unknown }>(),
    'Clear Selected Agent State': props<{ agentUuid?: string }>(),
    'Get Prompts': props<{ tenantUuid: string; agentUuid: string }>(),
    'Get Prompts Success': props<{ agentPrompts: AgentPromptDto[] }>(),
    'Get Prompts Failure': props<{ error: unknown }>(),
    'Update Prompt': props<{
      tenantUuid: string;
      agentUuid: string;
      agentPromptId: string;
      changes: CreateUpdateAgentPromptPayload;
    }>(),
    'Update Prompt Success': props<{ agentPrompt: AgentPromptDto }>(),
    'Update Prompt Failure': props<{ error: unknown }>(),
    'Create Prompt': props<{
      tenantUuid: string;
      agentUuid: string;
      changes: CreateUpdateAgentPromptPayload;
    }>(),
    'Create Prompt Success': props<{ agentPrompt: AgentPromptDto }>(),
    'Create Prompt Failure': props<{ error: unknown }>(),
    'Delete Prompt': props<{
      tenantUuid: string;
      agentUuid: string;
      agentPromptId: number;
    }>(),
    'Delete Prompt Success': props<{
      agentPromptId: number;
    }>(),
    'Delete Prompt Failure': props<{ error: unknown }>(),
    'Get Agent Inputs': props<{ tenantUuid: string; agentUuid: string }>(),
    'Get Agent Inputs Success': props<{ agentInputs: AgentInputDto[] }>(),
    'Get Agent Inputs Failure': props<{ error: unknown }>(),
    'Update Agent Input': props<{
      tenantUuid: string;
      agentUuid: string;
      agentInputId: number;
      changes: CreateUpdateAgentInputPayload;
    }>(),
    'Update Agent Input Success': props<{ agentInput: AgentInputDto }>(),
    'Update Agent Input Failure': props<{ error: unknown }>(),
    'Create Agent Input': props<{
      tenantUuid: string;
      agentUuid: string;
      changes: CreateUpdateAgentInputPayload[];
    }>(),
    'Create Agent Input Success': props<{ agentInputs: AgentInputDto[] }>(),
    'Create Agent Input Failure': props<{ error: unknown }>(),
    'Delete Agent Input': props<{
      tenantUuid: string;
      agentUuid: string;
      agentInputId: number;
    }>(),
    'Delete Agent Input Success': props<{
      agentInputId: number;
    }>(),
    'Delete Agent Input Failure': props<{ error: unknown }>(),
    'Get Agent Versions': props<{ tenantUuid: string; agentUuid: string }>(),
    'Get Agent Versions Success': props<{ agentVersions: AgentVersionHistoryDto[] }>(),
    'Get Agent Versions Failure': props<{ error: unknown }>(),
    'Create Agent Version': props<{ tenantUuid: string; agentUuid: string }>(),
    'Create Agent Version Success': props<{ tenantUuid: string; agentUuid: string }>(),
    'Create Agent Version Failure': props<{ error: unknown }>(),
    'Update Agent Version Status': props<{
      tenantUuid: string;
      agentUuid: string;
      versionId: number;
      status: AgentStatusType;
    }>(),
    'Update Agent Version Status Success': props<{ tenantUuid: string; agentUuid: string }>(),
    'Update Agent Version Status Failure': props<{ error: unknown }>(),
    'Update Agent Everything': props<{
      tenantUuid: string;
      agentUuid: string;
      agentChanges: UpdateAgentPayload;
      instructions: {
        create: CreateUpdateAgentInstructionPayload[];
        update: {
          agentInstructionId: number;
          changes: CreateUpdateAgentInstructionPayload;
        }[];
        delete: number[];
      };
      prompts: {
        create: CreateUpdateAgentPromptPayload[];
        update: { agentPromptId: string; changes: CreateUpdateAgentPromptPayload }[];
        delete: number[];
      };
      inputs: {
        create: CreateUpdateAgentInputPayload[];
        update: { agentInputId: number; changes: CreateUpdateAgentInputPayload }[];
        delete: number[];
      };
      createVersion?: boolean;
    }>(),
    'Update Agent Everything Success': props<{
      agent: AgentDto;
      createVersion?: boolean;
    }>(),
    'Update Agent Everything Failure': props<{ error: unknown }>(),
    Clear: emptyProps(),
  },
});
